import { Record } from 'immutable';
import differenceInDays from 'date-fns/differenceInDays';

import Hydrator from 'utils/Hydrator';

import { CompanyType } from 'enums/company-type';
import { maxDaysDifference } from 'enums/client-search-filter-type';
import { isEmpty } from 'utils/ObjectHelper';

import Insight from './InsightModel';
import SnapshotPreview from './SnapshotPreviewModel';
import CompanyAddress from './CompanyAddressModel';
import { CountryList } from 'enums/country';

const ClientRecord = Record({
  id: 0,
  entityId: 0,
  companyNumber: '',
  companyName: '',
  companyAddress: null,
  entityType: 0,
  introducerType: 0,
  companyType: 0,
  contactName: '',
  contactEmail: '',
  logo: '',
  lenderType: 0,
  description: '',
  financialInsight: null,
  sector: null,
  sectorName: '',
  directors: [],
  contacts: [],
  addresses: [],
  latestYearTurnover: 0,
  latestYearProfit: 0,
  membershipLevel: null,
  hasPrimaryUser: false,
  revenue: 0,
  tradingPeriod: 0,
  tradingFrom: new Date(0),
  tradingHistory: null,
  createdAt: new Date(0),
  updatedAt: new Date(0),
  preview: null,
  isInvited: false,
  assignedTags: [],
  note: {},
  managerUserId: null,
});

export default class Client extends ClientRecord {
  static deserialize(jsonObject = {}) {
    const mapProperties = {
      createdAt: Hydrator.timestamp2Date,
      preview: SnapshotPreview.deserialize,
      tradingFrom: Hydrator.timestamp2Date,
      updatedAt: Hydrator.timestamp2Date,
      companyAddress: CompanyAddress.deserialize,
      financialInsight: Insight.deserialize,
    };

    const hydrated = Object.keys(jsonObject).reduce(Hydrator.assign(jsonObject, mapProperties), {});

    return new Client(hydrated);
  }

  get isMonitorConnected() {
    if (!this.preview) return false;
    const { integrations } = this.preview;
    const maybeIntegration = integrations && (integrations.privateAccounting || integrations.bank);
    return !!maybeIntegration;
  }

  get isAccountingSolutionConnected() {
    return (
      !!this.preview?.integrations?.privateAccounting ||
      !!this.preview?.integrations?.publicAccounting
    );
  }

  get entityId() {
    return this.id;
  }

  get companyCountryCode() {
    return this.client?.countryCode ?? CountryList.GB;
  }

  get creditScore() {
    return +this.preview?.creditScoreGrade || 6;
  }

  get isRecentlyAdded() {
    const daysDifference = differenceInDays(new Date(), this.createdAt);

    return daysDifference < maxDaysDifference;
  }

  get isSoleTrader() {
    return this.companyType === CompanyType.soleTrader;
  }

  get generatedDate() {
    const oldDefaultDate = '1970-01-01';
    if (!this.preview) return oldDefaultDate;

    return this.preview.generatedAt;
  }

  get facilityUtilisation() {
    return !!this.preview && this.preview.providerCreditLinesAmount;
  }

  get managedBy() {
    return this.preview?.managedBy?.name || '';
  }

  get facilityExpiry() {
    return !!this.preview && this.preview.providerCreditLinesExpiryDate;
  }

  get nextRenewal() {
    return !!this.preview && this.preview.bankAccountStatementBalance;
  }

  get address() {
    return this.companyAddress ? this.companyAddress.address : '';
  }

  get cashPositionChange() {
    const cashPositionChange = this.preview?.financialInsight?.cashPositionChange;

    return cashPositionChange && cashPositionChange > 0 ? cashPositionChange : 0;
  }

  get equity() {
    const equity = this.preview?.balanceSheet?.equity;

    return equity || 0;
  }

  get roundedCashPosition() {
    const cashPositionChange = this.preview?.financialInsight?.cashPositionChange;
    if (!this.cashPositionChange || this.cashPositionChange < 0) {
      return 0;
    }
    const nearestCeilingAmount = 5000;
    return (
      Math.round(cashPositionChange / nearestCeilingAmount) * nearestCeilingAmount ||
      nearestCeilingAmount
    );
  }

  get insightType() {
    return this.preview?.financialInsight?.type || '';
  }

  get insightTag() {
    return this.preview?.financialInsight?.tag || '';
  }

  get quickRatio() {
    return +(this.preview?.ratios?.quick || 0);
  }

  get totalCash() {
    return this.preview?.balanceSheet?.totalCash || '';
  }

  get netAssets() {
    return this.preview?.balanceSheet?.netAssets || '';
  }

  get annualRevenue() {
    return this.preview?.annualRevenue || '';
  }

  get annualProfitNet() {
    return this.preview?.annualProfitNet || '';
  }

  get snapshotGeneratedAt() {
    return this.preview?.generatedAt || '';
  }

  get creditScoreGrade() {
    const creditScoreGrade = this.preview?.creditScoreGrade;
    if (!creditScoreGrade) {
      return 0;
    }

    return creditScoreGrade;
  }

  get withFacilities() {
    return (
      !isEmpty(this.preview?.bankAccountCreditLines || {}) ||
      !isEmpty(this.preview?.providerCreditLines || {})
    );
  }
}
